import React from 'react';
import {
  Ban,
  Link2,
  UserPlus,
  Users,
  ShieldOff,
  Play,
  Image,
  CheckCircle2,
  XCircle,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  ExternalLink,
  Clock,
} from 'lucide-react';
import {
  SectionCard,
  MetricCard,
  TabLoading,
  TabError,
  TabEmpty,
  UserMiniCard,
  StatusBadge,
} from './shared';
import {
  useUserFollowersAndFollowing,
  useUserBlockedUsers,
  useUserConnectedAccounts,
} from '../../hooks/useUserTabs';
import { cn, formatRelativeTime, formatCompactNumber } from '../../utils/helpers';
import type {
  FollowUser,
  BlockedUserEntry,
  BlockedByUserEntry,
  ConnectedAccount,
  TikTokVideoItem,
  InstagramMediaItem,
} from '../../types/userTabs';

// ============================================================================
// Sub-components
// ============================================================================

interface SocialTabProps {
  userId: number;
}

const FollowUserList: React.FC<{
  users: FollowUser[];
  emptyMessage: string;
}> = ({ users, emptyMessage }) => {
  if (!users || users.length === 0) {
    return (
      <div className="py-6 text-center">
        <p className="text-xs text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {users.map((user) => {
        const location = [user.city, user.state].filter(Boolean).join(', ');
        const subtitle = [location, user.followedAt ? formatRelativeTime(user.followedAt) : null].filter(Boolean).join(' · ');

        return (
          <UserMiniCard
            key={user.id}
            user={{
              id: user.id,
              name: user.name,
              profileImage: user.profileImage,
            }}
            subtitle={subtitle || undefined}
            badge={
              <div className="flex items-center gap-1.5">
                {user.isVerified && (
                  <CheckCircle2 className="h-3.5 w-3.5 text-blue-400" />
                )}
                {user.isOnline && (
                  <span className="h-2 w-2 rounded-full bg-emerald-400" />
                )}
              </div>
            }
          />
        );
      })}
    </div>
  );
};

const PlatformIcon: React.FC<{ provider: string }> = ({ provider }) => {
  const p = (provider || '').toLowerCase();
  const colorClass =
    p === 'tiktok'
      ? 'text-pink-400 bg-pink-500/10'
      : p === 'instagram'
        ? 'text-purple-400 bg-purple-500/10'
        : p === 'facebook'
          ? 'text-blue-400 bg-blue-500/10'
          : p === 'twitter' || p === 'x'
            ? 'text-sky-400 bg-sky-500/10'
            : 'text-muted-foreground bg-muted/20';

  return (
    <div className={cn('p-2 rounded-lg flex-shrink-0', colorClass)}>
      <Link2 className="h-3.5 w-3.5" />
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const SocialTab: React.FC<SocialTabProps> = ({ userId }) => {
  const {
    followers: followersData,
    following: followingData,
    loading: followersLoading,
    error: followersError,
    refetch: refetchFollowers,
  } = useUserFollowersAndFollowing(userId, true);

  const {
    data: blockedData,
    loading: blockedLoading,
    error: blockedError,
    refetch: refetchBlocked,
  } = useUserBlockedUsers(userId, true);

  const {
    data: connectedData,
    loading: connectedLoading,
    error: connectedError,
    refetch: refetchConnected,
  } = useUserConnectedAccounts(userId, true);

  const isLoading = followersLoading || blockedLoading || connectedLoading;
  const firstError = followersError || blockedError || connectedError;

  if (isLoading) return <TabLoading />;
  if (firstError) {
    return (
      <TabError
        message={firstError}
        onRetry={() => {
          refetchFollowers();
          refetchBlocked();
          refetchConnected();
        }}
      />
    );
  }

  const followersCount = followersData?.total ?? followersData?.items?.length ?? 0;
  const followingCount = followingData?.total ?? followingData?.items?.length ?? 0;
  const blockedCount = blockedData?.blockedCount ?? blockedData?.blockedUsers?.length ?? 0;
  const blockedByCount = blockedData?.blockedByCount ?? blockedData?.blockedByUsers?.length ?? 0;
  const accountsCount = connectedData?.connectedAccounts?.length ?? 0;

  const hasNoData =
    followersCount === 0 &&
    followingCount === 0 &&
    blockedCount === 0 &&
    blockedByCount === 0 &&
    accountsCount === 0;

  if (hasNoData && !followersData && !followingData && !blockedData && !connectedData) {
    return <TabEmpty message="No social data available" icon={Users} />;
  }

  return (
    <div className="space-y-5">
      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricCard
          label="Followers"
          value={followersCount}
          icon={Users}
          color="text-violet-400"
          bgColor="bg-violet-500/10"
        />
        <MetricCard
          label="Following"
          value={followingCount}
          icon={UserPlus}
          color="text-indigo-400"
          bgColor="bg-indigo-500/10"
        />
        <MetricCard
          label="Blocked Users"
          value={blockedCount + blockedByCount}
          icon={Ban}
          color="text-red-400"
          bgColor="bg-red-500/10"
          subtitle={
            blockedCount > 0 || blockedByCount > 0
              ? `${blockedCount} blocked, ${blockedByCount} blocked by`
              : undefined
          }
        />
        <MetricCard
          label="Connected Accounts"
          value={accountsCount}
          icon={Link2}
          color="text-blue-400"
          bgColor="bg-blue-500/10"
        />
      </div>

      {/* Followers / Following Section */}
      {(followersCount > 0 || followingCount > 0) && (
        <SectionCard
          title="Followers & Following"
          icon={Users}
          subtitle={`${followersCount} follower${followersCount !== 1 ? 's' : ''}, ${followingCount} following`}
          collapsible
          defaultOpen
        >
          <div className="space-y-5">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-3.5 w-3.5 text-violet-400" />
                <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  Followers
                </h4>
                <span className="text-[10px] text-muted-foreground">({followersCount})</span>
              </div>
              <FollowUserList
                users={followersData?.items ?? []}
                emptyMessage="No followers"
              />
            </div>

            <div className="pt-3 border-t border-border/30">
              <div className="flex items-center gap-2 mb-3">
                <UserPlus className="h-3.5 w-3.5 text-indigo-400" />
                <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  Following
                </h4>
                <span className="text-[10px] text-muted-foreground">({followingCount})</span>
              </div>
              <FollowUserList
                users={followingData?.items ?? []}
                emptyMessage="Not following anyone"
              />
            </div>
          </div>
        </SectionCard>
      )}

      {/* Blocked Users Section */}
      {(blockedCount > 0 || blockedByCount > 0) && (
        <SectionCard
          title="Blocked Users"
          icon={Ban}
          subtitle={`${blockedCount} blocked, ${blockedByCount} blocked by`}
          collapsible
          defaultOpen
        >
          <div className="space-y-5">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ShieldOff className="h-3.5 w-3.5 text-red-400" />
                <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  Users Blocked
                </h4>
                <span className="text-[10px] text-muted-foreground">({blockedCount})</span>
              </div>
              {blockedData?.blockedUsers && blockedData.blockedUsers.length > 0 ? (
                <div className="space-y-2.5">
                  {blockedData.blockedUsers.map((entry: BlockedUserEntry) => (
                    <UserMiniCard
                      key={entry.id}
                      user={{
                        id: entry.blocked.id,
                        name: entry.blocked.name,
                        profileImage: entry.blocked.profileImage,
                      }}
                      subtitle={
                        [entry.reason, entry.createdAt ? formatRelativeTime(entry.createdAt) : null]
                          .filter(Boolean)
                          .join(' · ') || undefined
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className="py-6 text-center">
                  <p className="text-xs text-muted-foreground">No users blocked</p>
                </div>
              )}
            </div>

            <div className="pt-3 border-t border-border/30">
              <div className="flex items-center gap-2 mb-3">
                <Ban className="h-3.5 w-3.5 text-orange-400" />
                <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  Blocked By
                </h4>
                <span className="text-[10px] text-muted-foreground">({blockedByCount})</span>
              </div>
              {blockedData?.blockedByUsers && blockedData.blockedByUsers.length > 0 ? (
                <div className="space-y-2.5">
                  {blockedData.blockedByUsers.map((entry: BlockedByUserEntry) => (
                    <UserMiniCard
                      key={entry.id}
                      user={{
                        id: entry.user.id,
                        name: entry.user.name,
                        profileImage: entry.user.profileImage,
                      }}
                      subtitle={
                        [entry.reason, entry.createdAt ? formatRelativeTime(entry.createdAt) : null]
                          .filter(Boolean)
                          .join(' · ') || undefined
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className="py-6 text-center">
                  <p className="text-xs text-muted-foreground">Not blocked by anyone</p>
                </div>
              )}
            </div>
          </div>
        </SectionCard>
      )}

      {/* Connected Accounts Section */}
      {(accountsCount > 0 ||
        (connectedData?.tiktokVideos?.length ?? 0) > 0 ||
        (connectedData?.instagramMedia?.length ?? 0) > 0) && (
        <SectionCard
          title="Connected Accounts"
          icon={Link2}
          subtitle={`${accountsCount} account${accountsCount !== 1 ? 's' : ''} connected`}
          collapsible
          defaultOpen
        >
          <div className="space-y-5">
            {/* Account List */}
            {connectedData?.connectedAccounts && connectedData.connectedAccounts.length > 0 && (
              <div className="space-y-2.5">
                {connectedData.connectedAccounts.map((account: ConnectedAccount) => {
                  const isTokenExpired = account.tokenExpiresAt
                    ? new Date(account.tokenExpiresAt) < new Date()
                    : false;

                  return (
                    <div
                      key={account.id}
                      className="flex items-center gap-3 p-3 rounded-lg border border-border/50 bg-muted/5"
                    >
                      {account.avatarUrl ? (
                        <img
                          src={account.avatarUrl}
                          alt=""
                          className="h-9 w-9 rounded-full object-cover flex-shrink-0"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <PlatformIcon provider={account.provider} />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground capitalize">
                            {account.provider}
                          </p>
                          {!isTokenExpired ? (
                            <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                          ) : (
                            <XCircle className="h-3 w-3 text-red-400" />
                          )}
                        </div>
                        {account.displayName && (
                          <p className="text-[11px] text-muted-foreground">
                            @{account.displayName}
                          </p>
                        )}
                        {account.scope && (
                          <p className="text-[10px] text-muted-foreground/60 mt-0.5 truncate">
                            {account.scope}
                          </p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <StatusBadge
                          status={isTokenExpired ? 'Expired' : 'Active'}
                          variant={isTokenExpired ? 'error' : 'success'}
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {formatRelativeTime(account.connectedAt)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* TikTok Videos */}
            {connectedData?.tiktokVideos && connectedData.tiktokVideos.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3 pt-3 border-t border-border/30">
                  <Play className="h-3.5 w-3.5 text-pink-400" />
                  <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    TikTok Videos
                  </h4>
                  <span className="text-[10px] text-muted-foreground">
                    ({connectedData.tiktokVideos.length})
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {connectedData.tiktokVideos.map((video: TikTokVideoItem) => (
                    <div
                      key={video.id}
                      className="rounded-lg border border-border/50 bg-muted/5 overflow-hidden"
                    >
                      {video.coverImageUrl ? (
                        <div className="aspect-[9/16] bg-muted/20 overflow-hidden relative">
                          <img
                            src={video.coverImageUrl}
                            alt={video.title || 'TikTok video'}
                            className="h-full w-full object-cover"
                          />
                          {video.duration != null && (
                            <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
                              {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="aspect-[9/16] bg-muted/20 flex items-center justify-center">
                          <Play className="h-6 w-6 text-muted-foreground/30" />
                        </div>
                      )}
                      <div className="p-2.5 space-y-1.5">
                        <p className="text-xs font-medium text-foreground line-clamp-2">
                          {video.title || 'Untitled'}
                        </p>
                        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                          <span className="flex items-center gap-0.5">
                            <Eye className="h-2.5 w-2.5" />
                            {formatCompactNumber(video.viewCount)}
                          </span>
                          <span className="flex items-center gap-0.5">
                            <Heart className="h-2.5 w-2.5" />
                            {formatCompactNumber(video.likeCount)}
                          </span>
                          <span className="flex items-center gap-0.5">
                            <MessageCircle className="h-2.5 w-2.5" />
                            {video.commentCount}
                          </span>
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                          {formatRelativeTime(video.createTime)}
                        </p>
                        {video.shareUrl && (
                          <a
                            href={video.shareUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] text-primary hover:text-primary/80 transition-colors"
                          >
                            <ExternalLink className="h-2.5 w-2.5" />
                            View on TikTok
                          </a>
                        )}
                        {video.selectedForImport && (
                          <StatusBadge
                            status={video.importedAt ? 'Imported' : 'Selected'}
                            variant={video.importedAt ? 'success' : 'warning'}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Instagram Media */}
            {connectedData?.instagramMedia && connectedData.instagramMedia.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3 pt-3 border-t border-border/30">
                  <Image className="h-3.5 w-3.5 text-purple-400" />
                  <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Instagram Media
                  </h4>
                  <span className="text-[10px] text-muted-foreground">
                    ({connectedData.instagramMedia.length})
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {connectedData.instagramMedia.map((media: InstagramMediaItem) => (
                    <div
                      key={media.id}
                      className="rounded-lg border border-border/50 bg-muted/5 overflow-hidden"
                    >
                      {media.thumbnailUrl ? (
                        <div className="aspect-square bg-muted/20 overflow-hidden">
                          <img
                            src={media.thumbnailUrl}
                            alt={media.caption || 'Instagram media'}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="aspect-square bg-muted/20 flex items-center justify-center">
                          <Image className="h-6 w-6 text-muted-foreground/30" />
                        </div>
                      )}
                      <div className="p-2.5">
                        {media.mediaType && (
                          <StatusBadge status={media.mediaType} variant="default" />
                        )}
                        {media.caption && (
                          <p className="text-xs text-muted-foreground truncate mt-1">
                            {media.caption}
                          </p>
                        )}
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {formatRelativeTime(media.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* Show empty state if all sections are empty but data was fetched */}
      {hasNoData && (
        <TabEmpty message="No social connections found for this user" icon={Users} />
      )}
    </div>
  );
};

export default SocialTab;
